import React, { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";

const TRIGGER = 80;
const MAX = 130;
const RESISTANCE = 0.55;

/**
 * iOS-style soft pull-to-refresh, mobile + tablet only.
 *
 * Listens to passive touch events on the document. When the user starts a
 * touchstart at scrollY === 0 and drags downward, we draw a small pill
 * that drops in from above the viewport. Past the trigger threshold the
 * icon flips active; releasing triggers a hard reload of the page.
 *
 * No-op on devices without touch (desktop / laptop).
 */
export default function PullToRefresh() {
  const [pullY, setPullY] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startYRef = useRef(0);
  const pullingRef = useRef(false);
  const pullYRef = useRef(0);

  useEffect(() => {
    pullYRef.current = pullY;
  }, [pullY]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    // Only enable on devices with a coarse pointer (touchscreens) AND
    // at mobile/tablet widths. Skip on desktop entirely.
    const isCoarse =
      window.matchMedia && window.matchMedia("(hover: none) and (pointer: coarse)").matches;
    if (!isCoarse) return undefined;

    const onTouchStart = (e) => {
      // Only start a pull when we're at the very top of the page and not
      // already refreshing. Multi-touch (pinch) is ignored.
      if (refreshing) return;
      if (window.scrollY > 0) {
        pullingRef.current = false;
        return;
      }
      if (e.touches.length !== 1) return;
      // Skip pulls that begin inside a scrollable element (chat panes,
      // drawers etc) or on form fields where the gesture would conflict.
      const target = e.target;
      if (
        target &&
        target.closest &&
        target.closest(
          "input, textarea, select, [contenteditable], [data-no-pull-to-refresh]"
        )
      ) {
        pullingRef.current = false;
        return;
      }
      startYRef.current = e.touches[0].clientY;
      pullingRef.current = true;
    };

    const onTouchMove = (e) => {
      if (!pullingRef.current || refreshing) return;
      // If the user has scrolled away from the top mid-gesture, abort.
      if (window.scrollY > 0) {
        pullingRef.current = false;
        if (pullYRef.current !== 0) setPullY(0);
        return;
      }
      const dy = e.touches[0].clientY - startYRef.current;
      if (dy <= 0) {
        if (pullYRef.current !== 0) setPullY(0);
        return;
      }
      const eased = Math.min(MAX, dy * RESISTANCE);
      setPullY(eased);
    };

    const onTouchEnd = () => {
      if (!pullingRef.current) return;
      pullingRef.current = false;
      const distance = pullYRef.current;
      if (distance >= TRIGGER) {
        // Lock at the trigger position, vibrate, and reload after a short
        // settle so the user clearly sees the spinner kick in.
        setPullY(TRIGGER);
        setRefreshing(true);
        try { navigator.vibrate?.(15); } catch (_) { /* ignore */ }
        setTimeout(() => {
          window.location.reload();
        }, 360);
      } else {
        setPullY(0);
      }
    };

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: true });
    document.addEventListener("touchend", onTouchEnd, { passive: true });
    document.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [refreshing]);

  // The pill rests at -56px (above viewport) when idle and slides down by
  // pullY pixels. Active state once the user has pulled past the trigger.
  const active = refreshing || pullY >= TRIGGER;
  const visible = pullY > 0 || refreshing;
  const translate = -56 + pullY;
  const rotation = refreshing ? 0 : Math.min(360, (pullY / TRIGGER) * 270);

  return (
    <div
      aria-hidden={!visible}
      className="fixed top-0 inset-x-0 grid place-items-center pointer-events-none z-[200]"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
      data-testid="pull-to-refresh"
    >
      <div
        className={`flex items-center justify-center w-11 h-11 rounded-full bg-white border border-slate-200 shadow-lg transition-[transform,opacity,background-color,color] ${
          visible ? "opacity-100" : "opacity-0"
        } ${active ? "bg-sky-600 border-sky-600 text-white" : "text-slate-500"}`}
        style={{
          transform: `translate3d(0, ${translate}px, 0)`,
          transitionDuration: pullingRef.current ? "0ms" : "260ms",
        }}
        data-testid="pull-to-refresh-indicator"
        data-active={active ? "true" : "false"}
      >
        <RefreshCw
          className={`w-5 h-5 ${refreshing ? "animate-spin" : ""}`}
          style={{ transform: refreshing ? undefined : `rotate(${rotation}deg)` }}
          strokeWidth={2.4}
        />
      </div>
    </div>
  );
}
